// @ts-strict-ignore
import React, { useMemo, useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useAtomValue, useSetAtom } from 'jotai'
import { SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDebounce } from '@node-space/hooks'
import { Avatar, RadioOption } from '@node-space/ui-components'
import { logSentryErrorAndGetEventId } from '@node-space/utils'
import { SEARCH_INPUT_DEBOUNCE_TIME } from 'constants/General'
import { useFormFields } from 'hooks/forms/useFormFields'
import { useCreatePaymentMutation } from 'hooks/mutations/useCreatePaymentMutation'
import { useCryptoCurrenciesQuery } from 'hooks/queries/useCryptoCurrenciesQuery'
import { useFiatCurrenciesQuery } from 'hooks/queries/useFiatCurrenciesQuery'
import { useWalletsQueryV2 } from 'hooks/queries/useWalletsQueryV2'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { useSupportedCurrencies } from 'hooks/useSupportedCurrencies'
import { contactAtom } from 'pages/Contacts/atoms/contactAtoms'
import { getContactComplianceDetails } from 'pages/Contacts/utils/utils'
import { BaseErrorResponse } from 'types/beneficiaries'
import { PartyDetailsType, PaymentLinkStep, PaymentsCreateRequest } from 'types/payments'
import { generateReference, mapToOptions } from 'utils/utils'
import { paymentLinkStepAtom, paymentLinkUrlAtom } from '../atoms/atoms'
import {
  ExpirtyDateValues,
  PaymentLinkFormKeys,
  paymentLinkSchema,
} from '../schemas/paymentLinkSchema'

export const usePaymentLinkForm = () => {
  const { t } = useTranslation()
  const { getSupportedCurrencyWallets, getWalletOptions } = useSupportedCurrencies()
  const contact = useAtomValue(contactAtom)
  const formSchema = paymentLinkSchema(t)
  const setPaymentLinkStep = useSetAtom(paymentLinkStepAtom)
  const setPaymentLinkUrl = useSetAtom(paymentLinkUrlAtom)
  const { data: fiatCurrencies, isFetching: isFetchingFiatCurrencies } = useFiatCurrenciesQuery()
  const { data: cryptoCurrencies, isFetching: isFetchingCryptoCurrencies } =
    useCryptoCurrenciesQuery()
  const { mutate, isPending } = useCreatePaymentMutation()
  const { requestError, setRequestError, resetRequestError } = useMappedErrorMessage()
  const [searchWalletQuery, setSearchWalletQuery] = useState('')
  const debouncedSearchWalletTerm = useDebounce(searchWalletQuery, SEARCH_INPUT_DEBOUNCE_TIME)
  const { data: wallets, isFetching: isFetchingWallets } = useWalletsQueryV2({
    walletType: '',
    searchTerm: debouncedSearchWalletTerm,
    max: 20,
    order: 'asc',
    sort: 'description',
  })

  const activeWallets = getSupportedCurrencyWallets(wallets?.data)
  const [activeWallet, setActiveWallet] = useState(activeWallets?.[0] ?? null)
  const [walletError, setWalletError] = useState(false)

  const expiryDateOptions: RadioOption[] = [
    { value: 'minutes', label: t('minutes') },
    { value: 'days', label: t('days') },
  ]

  const form = useForm<PaymentsCreateRequest>({
    defaultValues: {
      walletId: '',
      currency: '',
      amount: '',
      returnUrl: '',
      reference: `${generateReference()}`,
      flow: 'DEFAULT',
      type: 'IN',
      expiryMinutes: '2880',
    },
    resolver: yupResolver(formSchema),
  })

  const {
    watch,
    control,
    handleSubmit,
    setValue,
    setError,
    formState: { errors },
  } = form

  const formValues = useWatch({ control })

  const { setInputField, setBaseField } = useFormFields<PaymentLinkFormKeys>(
    formSchema,
    form,
    formValues
  )

  const fiatCurrenciesOptions = mapToOptions(fiatCurrencies)
  const cryptoCurrenciesOptions = mapToOptions(cryptoCurrencies)
  const isFetchingCurrencies = isFetchingCryptoCurrencies || isFetchingFiatCurrencies

  const currenciesOptions = [...fiatCurrenciesOptions, ...cryptoCurrenciesOptions]
    .map(option => ({
      ...option,
      leftText: option.label,
      LeftElement: <Avatar name={option?.value?.toLowerCase()} size="small" />,
    }))
    .sort((x, y) => x.label.localeCompare(y.label))

  const walletOptions = useMemo(() => getWalletOptions(activeWallets), [activeWallets])

  const formFields: { [key in PaymentLinkFormKeys] } = {
    walletId: setBaseField({
      key: 'walletId',
      placeholder: t('select'),
    }),
    currency: setBaseField({
      key: 'currency',
      placeholder: t('select'),
    }),
    amount: setInputField({
      key: 'amount',
      placeholder: `${t('amount')}...`,
    }),
    reference: setInputField({
      key: 'reference',
    }),
    expiryMinutes: setBaseField({
      key: 'expiryMinutes',
    }),
    returnUrl: setInputField({
      key: 'returnUrl',
    }),
  }

  const onSubmit: SubmitHandler<PaymentsCreateRequest> = values => {
    const data: PaymentsCreateRequest = {
      ...values,
      customerId: contact?.id,
      complianceDetails: getContactComplianceDetails(contact, PartyDetailsType.ORIGINATOR),
    }

    resetRequestError()
    mutate(data, {
      onSuccess: res => {
        setPaymentLinkUrl(res?.redirectUrl)
        setPaymentLinkStep(PaymentLinkStep.COMPLETE)
      },
      onError: (error: BaseErrorResponse, data: PaymentsCreateRequest) => {
        const errorList = error?.data?.errorList
        const errorDetails = error?.data?.details?.errors ?? null
        const errorStatus = error?.status
        if (errorList?.length) {
          errorList?.forEach(axiosErr =>
            setError(axiosErr.parameter as keyof PaymentsCreateRequest, {
              message: axiosErr?.message,
            })
          )
          return
        }

        const requestError = error?.data?.message || t('payments.createPaymentForbidden')
        const sentryEventId = logSentryErrorAndGetEventId(
          `Failed API request to /v1/pay/summary with status ${error?.status} - useCreatePaymentMutation`,
          error,
          {
            data: typeof data === 'string' ? { message: data } : data,
          }
        )

        setRequestError({
          handledMessage: requestError,
          sentryTraceId: sentryEventId,
          show: true,
          errorCode: errorStatus,
          ErrorMessages: errorDetails,
        })
      },
    })
  }

  const handleOpen = (isOpen: boolean) => {
    if (!isOpen) {
      setSearchWalletQuery('')
    }
  }

  const handleWalletOnChange = (walletId: string) => {
    const selectedWallet = activeWallets?.find(activeWallet => {
      return activeWallet?.lsid == walletId
    })

    if (selectedWallet) {
      setActiveWallet(selectedWallet)
      setValue('walletId', selectedWallet?.lsid, { shouldValidate: true, shouldDirty: true })
      setValue('currency', selectedWallet?.currency?.code, {
        shouldValidate: true,
        shouldDirty: true,
      })
    }
    setWalletError(false)
    setSearchWalletQuery('')
  }

  const handleExpiryValueChange = (values: ExpirtyDateValues) => {
    let expiryValue = 0
    if (values.expiryType == 'days') {
      expiryValue = values.expiryAmount ? values.expiryAmount * 24 * 60 : 0
    }
    if (values.expiryType == 'minutes') {
      expiryValue = values.expiryAmount ? values.expiryAmount : 0
    }

    setValue('expiryMinutes', String(expiryValue), { shouldValidate: true, shouldDirty: true })
  }

  const isCurrencyMismatch =
    formValues?.walletId &&
    formValues?.currency &&
    formValues?.currency !== activeWallet?.currency?.code

  return {
    walletOptions,
    currenciesOptions,
    isFetchingWallets,
    isFetchingCurrencies,
    isCurrencyMismatch,
    errors,
    requestError,
    isPending,
    walletError,
    formFields,
    activeWallet,
    expiryDateOptions,
    handleExpiryValueChange,
    handleOpen,
    setSearchWalletQuery,
    watch,
    setValue,
    handleWalletOnChange,
    handleSubmit,
    onSubmit,
  }
}
